import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import colors from 'theme/colors';

const accent = colors.occasionalPurple;

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// `month` is a dayjs at the first day of the visible month; `days` maps
// 'YYYY-MM-DD' -> slots. Only keys present in `days` are selectable.
export default function AvailabilityCalendar({ month, days, selectedDate, onSelectDate, onChangeMonth, loading }) {
    const isCurrentMonth = !month.isAfter(dayjs().startOf('month'), 'month');
    const offset = month.day();
    const cells = [
        ...Array.from({ length: offset }, () => null),
        ...Array.from({ length: month.daysInMonth() }, (_, i) => month.date(i + 1).format('YYYY-MM-DD')),
    ];

    return (
        <Wrapper>
            <Header>
                <Arrow type='button' aria-label='Mês anterior' disabled={isCurrentMonth} onClick={() => onChangeMonth(-1)}>
                    <ChevronLeft size={22} />
                </Arrow>
                <Title>{MONTHS[month.month()]} {month.year()}</Title>
                <Arrow type='button' aria-label='Próximo mês' onClick={() => onChangeMonth(1)}>
                    <ChevronRight size={22} />
                </Arrow>
            </Header>

            <Grid $loading={loading}>
                {WEEKDAYS.map((label, i) => (
                    <Weekday key={label} $weekend={i === 0 ? 'sun' : i === 6 ? 'sat' : null}>{label}</Weekday>
                ))}
                {cells.map((iso, i) => {
                    if (!iso) return <span key={`blank-${i}`} />;
                    const available = Boolean(days[iso]?.length);
                    const selected = iso === selectedDate;
                    return (
                        <Day
                            key={iso}
                            type='button'
                            disabled={!available}
                            $selected={selected}
                            aria-pressed={selected}
                            aria-label={iso}
                            onClick={() => onSelectDate(iso)}
                        >
                            {Number(iso.slice(8))}
                        </Day>
                    );
                })}
            </Grid>
        </Wrapper>
    );
}

const Wrapper = styled.div``;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const Title = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const Arrow = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: none;
    color: ${accent};
    cursor: pointer;

    &:disabled { color: ${colors.nightLight}; opacity: 0.5; cursor: default; }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px 0;
    justify-items: center;
    opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
    transition: opacity 0.15s;
`;

const Weekday = styled.span`
    font-size: 12px;
    font-weight: 600;
    padding-bottom: 4px;
    color: ${({ $weekend }) => ($weekend === 'sun' ? colors.danger : $weekend === 'sat' ? colors.primary : colors.night)};
`;

const Day = styled.button`
    position: relative;
    width: 38px;
    height: 38px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: ${({ $selected }) => ($selected ? accent : 'transparent')};
    color: ${({ $selected }) => ($selected ? '#fff' : colors.nightDark)};
    font-size: 14px;
    font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
    font-family: inherit;
    cursor: pointer;

    &:disabled {
        color: ${colors.nightLight};
        cursor: default;
        text-decoration: line-through;
    }
`;
